import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Image as ImageIcon, Loader2, Play } from "lucide-react";
import apiClient from "@/lib/api-client";

/**
 * Foto, vídeo o documento dentro de una burbuja del inbox (22-sep-2026).
 * Mismo circuito que `ConversationAudioPlayer`: el binario vive en Meta y el
 * backend lo sirve autenticado, así que se descarga como blob (un <img src>
 * directo no manda el Bearer). Las fotos se cargan solas al entrar en
 * pantalla — abrir un hilo largo no dispara N descargas —; los vídeos y
 * documentos, al pulsar, porque pesan más. Pasados ~30 días Meta ya no lo
 * retiene: el backend responde 404 y aquí se enseña el aviso (con la
 * descripción de la IA si la foto era entrante).
 */
export default function ConversationMediaAttachment({
  mediaUrl,
  kind,
  filename,
  description,
}: {
  mediaUrl: string;
  kind: "image" | "video" | "document";
  /** Nombre con el que se descarga un documento. */
  filename?: string;
  /** Lo que la IA vio en una foto entrante: se enseña si la foto ya no está. */
  description?: string;
}) {
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const fetchBlob = useCallback(
    () => apiClient.get<Blob>(mediaUrl, { responseType: "blob" }).then((res) => res.data),
    [mediaUrl],
  );

  const load = useCallback(() => {
    setState("loading");
    fetchBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setObjectUrl(url);
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [fetchBlob]);

  // Documento: se descarga con su nombre (un objectURL en pestaña nueva
  // perdería el nombre y algunos tipos ni se previsualizan).
  const download = useCallback(() => {
    setState("loading");
    fetchBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || "documento";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        setState("idle");
      })
      .catch(() => setState("error"));
  }, [fetchBlob, filename]);

  useEffect(() => {
    if (kind !== "image" || state !== "idle") return;
    const box = boxRef.current;
    if (!box) return;
    if (typeof IntersectionObserver === "undefined") {
      load();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, [kind, state, load]);

  if (state === "error") {
    const label =
      kind === "image" ? "Foto no disponible" : kind === "video" ? "Vídeo no disponible" : "Documento no disponible";
    return (
      <span className="block text-xs italic opacity-75">
        {label} (WhatsApp lo conserva unos 30 días)
        {description && <span className="block mt-1 not-italic">{description}</span>}
      </span>
    );
  }

  if (kind === "document") {
    return (
      <button
        type="button"
        onClick={download}
        disabled={state === "loading"}
        className="flex items-center gap-2 text-sm font-medium text-left underline-offset-2 hover:underline disabled:opacity-60"
      >
        {state === "loading" ? (
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
        ) : (
          <FileText className="w-4 h-4 flex-shrink-0" />
        )}
        <span className="break-all">{filename || "Documento"}</span>
        <Download className="w-3.5 h-3.5 flex-shrink-0 opacity-70" />
      </button>
    );
  }

  if (state === "ready" && objectUrl) {
    return kind === "image" ? (
      <a href={objectUrl} target="_blank" rel="noopener noreferrer" title="Ver a tamaño completo">
        <img
          src={objectUrl}
          alt="Foto"
          className="block rounded-lg max-h-72 max-w-full object-contain"
        />
      </a>
    ) : (
      <video controls autoPlay src={objectUrl} className="block rounded-lg max-h-72 max-w-full" />
    );
  }

  if (kind === "image") {
    return (
      <div
        ref={boxRef}
        className="w-48 h-32 max-w-full rounded-lg bg-black/10 flex items-center justify-center"
      >
        {state === "loading" ? (
          <Loader2 className="w-5 h-5 animate-spin opacity-60" />
        ) : (
          <ImageIcon className="w-5 h-5 opacity-40" />
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={load}
      disabled={state === "loading"}
      className="flex items-center gap-2 text-sm font-medium underline-offset-2 hover:underline disabled:opacity-60"
    >
      {state === "loading" ? (
        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
      ) : (
        <Play className="w-4 h-4 flex-shrink-0" />
      )}
      {state === "loading" ? "Cargando vídeo…" : "Ver vídeo"}
    </button>
  );
}
